import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/custom/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Crown, Users, AlertTriangle, Heart, MessageCircle,
  Search, Ban, CheckCircle, Trash2, Send, Building, User,
  Bell, BarChart3, Shield, Eye, Flag, X, Siren, Share2, ExternalLink,
  Mail, Calendar, MapPin, MessageSquare, Send as SendIcon,
  Plus, Globe, Radio, ImagePlus, Pencil, Download,
} from "lucide-react";
import { ShareEmergencyButton } from "@/components/custom/ShareButtons";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = "#686af3", color = "#fff" }) {
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, border: "2px solid #2D3561" }}
    >{children}</span>
  );
}

function BBtn({ children, onClick, disabled, variant = "primary", className = "", testId, style = {} }) {
  const styles = {
    primary: { background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    secondary: { background: "#f64e0e", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    danger: { background: "#e8637a", color: "#fff", border: B.border, boxShadow: B.shadowSm },
    ghost: { background: "transparent", color: "#2D3561", border: "2px solid #ccc" },
    outline: { background: "#fff", color: "#2D3561", border: B.border },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-1.5 ${className}`}
      style={{ fontFamily: B.mono, ...styles[variant], ...style }}
    >
      {children}
    </button>
  );
}

const inputStyle = { border: "2px solid #2D3561", borderRadius: 0 };
const selectStyle = { border: "2px solid #2D3561", borderRadius: 0 };
const dialogStyle = { borderRadius: 0, border: B.border, boxShadow: B.shadow };

const fileUrl = (u) => (u && u.startsWith("/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const EMG_TYPES = [
  { value: "dana", label: "DANA" },
  { value: "inundacion", label: "Inundación" },
  { value: "incendio", label: "Incendio" },
  { value: "terremoto", label: "Terremoto" },
  { value: "huracan", label: "Huracán" },
  { value: "sequia", label: "Sequía" },
  { value: "nevada", label: "Nevada" },
  { value: "ataque", label: "Ataque" },
  { value: "abandono_masivo", label: "Abandono masivo" },
  { value: "epidemia", label: "Epidemia" },
  { value: "otro", label: "Otro" },
];

const EMG_COUNTRIES = [
  { value: "ES", label: "España" },
  { value: "EC", label: "Ecuador" },
  { value: "VE", label: "Venezuela" },
  { value: "CO", label: "Colombia" },
];

const VISIBILITY_META = {
  borrador: { label: "BORRADOR", bg: "#999" },
  interna: { label: "INTERNA", bg: "#fa9000" },
  publica: { label: "PÚBLICA", bg: "#8B9A2D" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [activity, setActivity] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ title: "", message: "", target: "all" });
  const [reportedSightings, setReportedSightings] = useState([]);
  const [shareStats, setShareStats] = useState(null);
  const [activeTab, setActiveTab] = useState("users");
  const [emergencyPersonnel, setEmergencyPersonnel] = useState({ pending: [], role_requests: [], verified: [] });
  const [verificationModal, setVerificationModal] = useState({ open: false, user: null, action: null, isRoleRequest: false });
  const [verificationNote, setVerificationNote] = useState("");
  const [processingVerification, setProcessingVerification] = useState(false);
  const [countryFilter, setCountryFilter] = useState("all"); // "all", "ES", "EC"
  const [showCreateEmg, setShowCreateEmg] = useState(false);
  const [emgForm, setEmgForm] = useState({ title: "", type: "otro", description: "", location_name: "", country: "ES", images: [] });
  const [creatingEmg, setCreatingEmg] = useState(false);
  const [uploadingEmgImage, setUploadingEmgImage] = useState(false);
  const [editingEmgId, setEditingEmgId] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const token = localStorage.getItem("ea_token");

  useEffect(() => {
    if (!token) {
      navigate("/auth");
      return;
    }
    
    const checkAdmin = async () => {
      try {
        const authHeaders = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/check`, { headers: authHeaders });
        if (!res.data.is_admin) {
          toast.error("Acceso restringido a administradores");
          navigate("/");
        } else {
          setIsAdmin(true);
          setLoading(false);
        }
      } catch (err) {
        toast.error("Error de autenticacion");
        navigate("/auth");
      }
    };
    
    checkAdmin();
  }, [token, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [statsRes, usersRes, emergenciesRes, activityRes, sightingsRes, shareRes, personnelRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users?role=${userFilter === 'all' ? '' : userFilter}&search=${userSearch}`, { headers }),
        axios.get(`${API}/admin/emergencies`, { headers }),
        axios.get(`${API}/admin/activity?days=7`, { headers }),
        axios.get(`${API}/admin/sightings`, { headers }).catch(() => ({ data: { sightings: [] } })),
        axios.get(`${API}/share-stats/global`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/emergency-personnel`, { headers }).catch(() => ({ data: { pending: [], verified: [] } })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setEmergencies(emergenciesRes.data.emergencies);
      setActivity(activityRes.data.activity);
      setReportedSightings(sightingsRes.data.sightings.filter(s => s.report_count > 0));
      setShareStats(shareRes.data);
      setEmergencyPersonnel(personnelRes.data);
    } catch (err) {
      toast.error("Error cargando datos");
    }
  }, [isAdmin, userFilter, userSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "suspended" ? "active" : "suspended";
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status: newStatus }, { headers });
      toast.success(`Usuario ${newStatus === 'suspended' ? 'suspendido' : 'activado'}`);
      loadData();
    } catch {
      toast.error("Error actualizando usuario");
    }
  };

  const updateEmergencyStatus = async (emergencyId, status) => {
    try {
      await axios.put(`${API}/admin/emergencies/${emergencyId}/status`, { status }, { headers });
      toast.success("Emergencia actualizada");
      loadData();
    } catch {
      toast.error("Error actualizando emergencia");
    }
  };

  const deleteEmergency = async (emergencyId) => {
    if (!window.confirm("¿Eliminar esta emergencia y todos sus datos?")) return;
    try {
      await axios.delete(`${API}/admin/emergencies/${emergencyId}`, { headers });
      toast.success("Emergencia eliminada");
      loadData();
    } catch {
      toast.error("Error eliminando emergencia");
    }
  };

  const publishToTelegram = async (emergencyId, event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await axios.post(`${API}/admin/telegram/publish-emergency/${emergencyId}`, {}, { headers });
      toast.success("¡Publicado en Telegram!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error publicando en Telegram");
    }
  };

  const WHATSAPP_CHANNEL_URL = "https://whatsapp.com/channel/0029VbCWhL21t90VVBCzzp1U";

  const publishToWhatsApp = (emergency, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const message = `*EMERGENCIA: ${emergency.title}*

${emergency.location_name || 'España'}

${emergency.description || 'Se necesita ayuda urgente.'}

Más info: ${window.location.origin}/needs?emergency=${emergency.emergency_id}

#EmergenciaAnimal #Animales`;

    navigator.clipboard.writeText(message).then(() => {
      toast.success("¡Mensaje copiado! Pégalo en el canal de WhatsApp", { duration: 4000 });
      window.open(WHATSAPP_CHANNEL_URL, "_blank");
    }).catch(() => {
      toast.error("No se pudo copiar el mensaje");
    });
  };

  const uploadEmgImages = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingEmgImage(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload`, fd, { headers });
        urls.push(res.data.url);
      }
      setEmgForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(`${urls.length} imagen(es) subida(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error subiendo imagen");
    } finally {
      setUploadingEmgImage(false);
    }
  };

  const createOfficialEmergency = async () => {
    if (!emgForm.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    setCreatingEmg(true);
    try {
      if (editingEmgId) {
        await axios.put(`${API}/admin/emergencies/${editingEmgId}/edit`, emgForm, { headers });
        toast.success("Emergencia actualizada");
      } else {
        await axios.post(`${API}/admin/emergencies`, emgForm, { headers });
        toast.success("Emergencia creada como borrador");
      }
      setShowCreateEmg(false);
      setEditingEmgId(null);
      setEmgForm({ title: "", type: "otro", description: "", location_name: "", country: "ES", images: [] });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando la emergencia");
    } finally {
      setCreatingEmg(false);
    }
  };

  const openEditEmergency = (emg, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setEditingEmgId(emg.emergency_id);
    setEmgForm({
      title: emg.title || "",
      type: emg.type || "otro",
      description: emg.description || "",
      location_name: emg.location_name || "",
      country: emg.country || "ES",
      images: emg.images || [],
    });
    setShowCreateEmg(true);
  };

  const downloadPoster = async (emergencyId, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const t = toast.loading("Generando cartel...");
    try {
      const res = await axios.post(`${API}/admin/emergencies/${emergencyId}/poster`, {}, { headers });
      toast.dismiss(t);
      toast.success("Cartel generado");
      window.open(fileUrl(res.data.poster_url), "_blank");
      loadData();
    } catch {
      toast.dismiss(t);
      toast.error("Error generando el cartel");
    }
  };

  const previewCleanup = async () => {
    try {
      const res = await axios.post(`${API}/admin/cleanup-test-data`, { dry_run: true }, { headers });
      setCleanupPreview(res.data);
    } catch {
      toast.error("Error preparando la limpieza");
    }
  };

  const confirmCleanup = async () => {
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup-test-data`, { dry_run: false }, { headers });
      const d = res.data.deleted;
      toast.success(`Limpieza completada: ${d.users} usuarios, ${d.emergencies} emergencias, ${d.needs} necesidades y ${d.offers} ofertas eliminadas`);
      setCleanupPreview(null);
      loadData();
    } catch {
      toast.error("Error ejecutando la limpieza");
    } finally {
      setCleaning(false);
    }
  };

  const distributeEmergency = async (emergencyId, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!window.confirm("¿Distribuir esta emergencia internamente? Se notificará a protectoras y voluntarios.")) return;
    try {
      const res = await axios.put(`${API}/admin/emergencies/${emergencyId}/distribute`, {}, { headers });
      toast.success(`Distribuida internamente. Notificadas ${res.data.recipients} personas`);
      loadData();
    } catch {
      toast.error("Error distribuyendo la emergencia");
    }
  };

  const publishEmergencyWeb = async (emergencyId, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!window.confirm("¿Publicar esta emergencia en la web como Emergencia Animal?")) return;
    try {
      await axios.put(`${API}/admin/emergencies/${emergencyId}/publish`, {}, { headers });
      toast.success("Publicada en la web como Emergencia Animal");
      loadData();
    } catch {
      toast.error("Error publicando la emergencia");
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastForm.message) {
      toast.error("El mensaje es obligatorio");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/broadcast`, broadcastForm, { headers });
      toast.success(`Notificación enviada a ${res.data.recipients} usuarios`);
      setShowBroadcast(false);
      setBroadcastForm({ title: "", message: "", target: "all" });
    } catch {
      toast.error("Error enviando notificación");
    }
  };

  const moderateSighting = async (sightingId, status) => {
    try {
      await axios.put(`${API}/admin/sightings/${sightingId}/status`, { status }, { headers });
      toast.success(`Avistamiento ${status === 'verificado' ? 'verificado' : 'rechazado'}`);
      loadData();
    } catch {
      toast.error("Error al moderar");
    }
  };

  const banUser = async (userId, isBanned) => {
    if (!window.confirm(`¿${isBanned ? 'Desbanear' : 'Banear'} a este usuario?`)) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/ban`, { is_banned: !isBanned }, { headers });
      toast.success(`Usuario ${!isBanned ? 'baneado' : 'desbaneado'}`);
      loadData();
    } catch {
      toast.error("Error al actualizar usuario");
    }
  };

  const grantSOSExtra = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/sos-extra`, { amount: 1 }, { headers });
      toast.success("SOS extra concedido");
      loadData();
    } catch {
      toast.error("Error al conceder SOS extra");
    }
  };

  const openVerificationModal = (user, action, isRoleRequest = false) => {
    setVerificationModal({ open: true, user, action, isRoleRequest });
    setVerificationNote("");
  };

  const closeVerificationModal = () => {
    setVerificationModal({ open: false, user: null, action: null, isRoleRequest: false });
    setVerificationNote("");
    setProcessingVerification(false);
  };

  const confirmVerification = async () => {
    if (!verificationModal.user || processingVerification) return;
    setProcessingVerification(true);
    
    const { user, action, isRoleRequest } = verificationModal;
    
    try {
      if (isRoleRequest) {
        // Handle role change request
        if (action === "verify") {
          await axios.put(`${API}/admin/emergency-role-request/${user.user_id}/approve`, {}, { headers });
          toast.success(`${user.name} ahora es Personal de Emergencias`);
        } else {
          await axios.put(`${API}/admin/emergency-role-request/${user.user_id}/reject`, { 
            reason: verificationNote || "No especificado" 
          }, { headers });
          toast.success(`Solicitud de ${user.name} rechazada`);
        }
      } else {
        // Handle new registration
        if (action === "verify") {
          await axios.put(`${API}/admin/emergency-personnel/${user.user_id}/verify`, {}, { headers });
          toast.success(`${user.name} verificado correctamente`);
        } else {
          await axios.put(`${API}/admin/emergency-personnel/${user.user_id}/reject`, { 
            reason: verificationNote || "No especificado" 
          }, { headers });
          toast.success(`Solicitud de ${user.name} rechazada`);
        }
      }
      closeVerificationModal();
      loadData();
    } catch (err) {
      toast.error("Error al procesar la verificación");
      setProcessingVerification(false);
    }
  };

  const CORPS_LABELS = {
    proteccion_civil: "Protección Civil",
    bomberos: "Bomberos",
    policia_nacional: "Policía Nacional",
    policia_local: "Policía Local",
    guardia_civil: "Guardia Civil",
    mossos: "Mossos d'Esquadra",
    ertzaintza: "Ertzaintza",
    policia_foral: "Policía Foral",
    agents_rurals: "Agents Rurals / Forestales",
    sanitarios: "Sanitarios / SAMUR",
    cruz_roja: "Cruz Roja",
    veterinarios: "Veterinarios de Emergencias",
    otro: "Otro",
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8f7f4" }}>
        <div className="w-12 h-12 flex items-center justify-center animate-pulse" style={{ background: "#686af3", border: B.border }}>
          <Crown className="w-6 h-6 text-white" />
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const pendingCount = (emergencyPersonnel.pending?.length || 0) + (emergencyPersonnel.role_requests?.length || 0);

  const tabs = [
    { id: "users", label: "USUARIOS", icon: Users },
    { id: "emergencies", label: "EMERGENCIAS", icon: AlertTriangle },
    { id: "personnel", label: `PERSONAL EMG ${pendingCount > 0 ? `(${pendingCount})` : ''}`, icon: Siren },
    { id: "activity", label: "ACTIVIDAD", icon: BarChart3 },
    { id: "moderation", label: `MODERACIÓN ${reportedSightings.length > 0 ? `(${reportedSightings.length})` : ''}`, icon: Shield },
    { id: "shares", label: "DIFUSIÓN", icon: Share2 },
  ];

  return (
    <div className="min-h-screen" data-testid="admin-dashboard" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-28 pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center" style={{ background: "#686af3", border: B.border }}>
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "#2D3561" }}>
                PANEL DE <span style={{ color: "#686af3" }}>ADMIN</span>
              </h1>
              <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                // BIENVENIDA, JEFA SUPREMA
              </p>
            </div>
          </div>
          <BBtn onClick={() => setShowBroadcast(true)} variant="secondary" testId="broadcast-btn">
            <Send className="w-4 h-4" />
            NOTIFICACIÓN MASIVA
          </BBtn>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatsCard icon={Users} label="USUARIOS" value={stats.users.total} subtext={`+${stats.users.new_this_week} esta semana`} color="#686af3" />
            <StatsCard icon={AlertTriangle} label="EMERGENCIAS" value={stats.emergencies.active} subtext={`${stats.emergencies.total} totales`} color="#f64e0e" />
            <StatsCard icon={Heart} label="AYUDAS" value={stats.offers.accepted} subtext={`${stats.offers.success_rate}% éxito`} color="#e8637a" />
            <StatsCard icon={MessageCircle} label="MENSAJES" value={stats.engagement.messages} subtext={`${stats.engagement.conversations} convos`} color="#a88efb" />
            <StatsCard icon={Share2} label="COMPARTIDOS" value={shareStats?.total_shares || 0} subtext={`+${shareStats?.weekly_shares || 0} semana`} color="#8B9A2D" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-0 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors"
              style={{
                fontFamily: B.mono,
                background: activeTab === tab.id ? "#2D3561" : "#fff",
                color: activeTab === tab.id ? "#f8f7f4" : "#2D3561",
                border: B.border,
                marginRight: "-3px",
              }}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Users Tab */}
        {activeTab === "users" && (
          <BCard className="p-5 md:p-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#a88efb" }} />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10"
                  style={inputStyle}
                  data-testid="user-search"
                />
              </div>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[180px]" style={selectStyle} data-testid="user-filter-select">
                  <SelectValue placeholder="Filtrar por rol" />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 0, border: B.border }}>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="particular">Voluntarios</SelectItem>
                  <SelectItem value="protectora">Protectoras</SelectItem>
                </SelectContent>
              </Select>
              <BBtn onClick={previewCleanup} variant="danger" testId="cleanup-test-data-btn">
                <Trash2 className="w-4 h-4" />
                LIMPIAR DATOS DE PRUEBA
              </BBtn>
            </div>

            <div className="space-y-3">
              {users.map((u) => (
                <div
                  key={u.user_id}
                  className="flex items-center justify-between p-4"
                  style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                  data-testid={`user-row-${u.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center"
                      style={{ background: u.role === 'protectora' ? '#686af3' : '#a88efb', border: "2px solid #2D3561" }}>
                      {u.role === 'protectora' ? (
                        <Building className="w-5 h-5 text-white" />
                      ) : (
                        <User className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: "#2D3561" }}>
                        {u.organization_name || u.name}
                      </p>
                      <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right hidden sm:block">
                      {u.role === 'particular' ? (
                        <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                          {u.accepted_offers || 0} ayudas
                        </p>
                      ) : (
                        <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                          {u.emergencies_count || 0} emergencias
                        </p>
                      )}
                    </div>
                    <BBadge bg={u.role === 'protectora' ? '#686af3' : '#a88efb'}>
                      {u.role === 'protectora' ? 'PROTECTORA' : 'VOLUNTARIO'}
                    </BBadge>
                    {u.is_banned && (
                      <BBadge bg="#e8637a">
                        BANEADO
                      </BBadge>
                    )}
                    <button
                      onClick={() => banUser(u.user_id, u.is_banned)}
                      className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-white"
                      style={{ border: "2px solid #2D3561" }}
                      title={u.is_banned ? 'Desbanear' : 'Banear'}
                      data-testid={`ban-toggle-${u.user_id}`}
                    >
                      {u.is_banned ? <CheckCircle className="w-4 h-4" style={{ color: "#8B9A2D" }} /> : <Ban className="w-4 h-4" style={{ color: "#e8637a" }} />}
                    </button>
                    {u.role === 'protectora' && (
                      <button
                        onClick={() => grantSOSExtra(u.user_id)}
                        className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-white"
                        style={{ border: "2px solid #2D3561" }}
                        title={`Conceder SOS extra (tiene ${u.sos_extra_allowed || 0})`}
                        data-testid={`sos-extra-${u.user_id}`}
                      >
                        <Siren className="w-4 h-4" style={{ color: "#f64e0e" }} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-sm" style={{ color: "#999" }}>No se encontraron usuarios</p>
                </div>
              )}
            </div>
          </BCard>
        )}

        {/* Emergencies Tab */}
        {activeTab === "emergencies" && (
          <BCard className="p-5 md:p-6">
            {/* Country Filter + Create */}
            <div className="flex flex-wrap items-center gap-4 mb-6 pb-4" style={{ borderBottom: "2px solid #2D3561" }}>
              <span className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                FILTRAR POR PAÍS:
              </span>
              <div className="flex gap-2">
                {[
                  { code: "all", label: "TODOS" },
                  { code: "ES", label: "ESPAÑA" },
                  { code: "EC", label: "ECUADOR" },
                ].map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCountryFilter(c.code)}
                    className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all"
                    style={{
                      background: countryFilter === c.code ? "#2D3561" : "#f8f7f4",
                      color: countryFilter === c.code ? "#fff" : "#2D3561",
                      border: "2px solid #2D3561",
                    }}
                    data-testid={`country-filter-${c.code}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <BBtn onClick={() => {
                setEditingEmgId(null);
                setEmgForm({ title: "", type: "otro", description: "", location_name: "", country: "ES", images: [] });
                setShowCreateEmg(true);
              }} variant="secondary" className="ml-auto" testId="create-emergency-btn">
                <Plus className="w-4 h-4" />
              CREAR EMERGENCIA
              </BBtn>
            </div>

            <div className="space-y-3">
              {emergencies
                .filter(e => countryFilter === "all" || e.country === countryFilter || (!e.country && countryFilter === "ES"))
                .map((e) => (
                <Link
                  key={e.emergency_id}
                  to={`/needs?emergency=${e.emergency_id}`}
                  className="block p-4 transition-all duration-150 hover:-translate-y-0.5 cursor-pointer group"
                  style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                  data-testid={`emergency-row-${e.emergency_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <BBadge bg="#686af3">{e.country === "EC" ? "EC" : e.country || "ES"}</BBadge>
                        {e.visibility && VISIBILITY_META[e.visibility] && (
                          <BBadge bg={VISIBILITY_META[e.visibility].bg}>{VISIBILITY_META[e.visibility].label}</BBadge>
                        )}
                        {e.is_official && <BBadge bg="#f64e0e">OFICIAL</BBadge>}
                        <BBadge bg={e.status === 'activo' ? '#8B9A2D' : '#999'}>
                          {e.status}
                        </BBadge>
                        <BBadge bg="#2D3561">{e.type}</BBadge>
                        {e.priority === "urgente" && (
                          <BBadge bg="#f64e0e">URGENTE</BBadge>
                        )}
                        <span className="text-[10px] tracking-wider flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" 
                          style={{ fontFamily: B.mono, color: "#686af3" }}>
                          <ExternalLink className="w-3 h-3" /> VER DETALLE
                        </span>
                      </div>
                      <p className="text-sm font-bold" style={{ color: "#2D3561" }}>{e.title}</p>
                      <p className="text-[10px] tracking-wider mt-1" style={{ fontFamily: B.mono, color: "#999" }}>
                        {e.shelter_name} · {e.location_name}
                      </p>
                      {e.affected_provinces && (
                        <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: "#686af3" }}>
                          Provincias: {e.affected_provinces.join(", ")}
                        </p>
                      )}
                      {e.images?.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {e.images.slice(0, 4).map((img, i) => (
                            <img key={i} src={fileUrl(img)} alt={`Imagen ${i + 1} de la emergencia`}
                              className="w-12 h-12 object-cover" style={{ border: "2px solid #2D3561" }} />
                          ))}
                          {e.images.length > 4 && (
                            <span className="text-[10px] self-end" style={{ fontFamily: B.mono, color: "#999" }}>+{e.images.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4" onClick={(ev) => ev.preventDefault()}>
                      <button
                        onClick={(ev) => openEditEmergency(e, ev)}
                        className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-white"
                        style={{ border: "2px solid #2D3561" }}
                        title="Editar emergencia"
                        data-testid={`edit-emergency-${e.emergency_id}`}
                      >
                        <Pencil className="w-4 h-4" style={{ color: "#2D3561" }} />
                      </button>
                      <button
                        onClick={(ev) => downloadPoster(e.emergency_id, ev)}
                        className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#686af315]"
                        style={{ border: "2px solid #686af3" }}
                        title="Descargar cartel para redes (con la paleta editorial)"
                        data-testid={`poster-emergency-${e.emergency_id}`}
                      >
                        <Download className="w-4 h-4" style={{ color: "#686af3" }} />
                      </button>
                      {e.is_official && e.visibility === "borrador" && (
                        <button
                          onClick={(ev) => distributeEmergency(e.emergency_id, ev)}
                          className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#fa900015]"
                          style={{ border: "2px solid #fa9000" }}
                          title="Distribuir internamente (protectoras y voluntarios)"
                          data-testid={`distribute-emergency-${e.emergency_id}`}
                        >
                          <Radio className="w-4 h-4" style={{ color: "#fa9000" }} />
                        </button>
                      )}
                      {e.is_official && e.visibility !== "publica" && (
                        <button
                          onClick={(ev) => publishEmergencyWeb(e.emergency_id, ev)}
                          className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#8B9A2D15]"
                          style={{ border: "2px solid #8B9A2D" }}
                          title="Publicar en la web como Emergencia Animal"
                          data-testid={`publish-web-emergency-${e.emergency_id}`}
                        >
                          <Globe className="w-4 h-4" style={{ color: "#8B9A2D" }} />
                        </button>
                      )}
                      <Select
                        value={e.status}
                        onValueChange={(v) => updateEmergencyStatus(e.emergency_id, v)}
                      >
                        <SelectTrigger className="w-[110px] h-8 text-[10px]" style={selectStyle} data-testid={`status-select-${e.emergency_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ borderRadius: 0, border: B.border }}>
                          <SelectItem value="activo">Activo</SelectItem>
                          <SelectItem value="cerrado">Cerrado</SelectItem>
                          <SelectItem value="archivado">Archivado</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={(ev) => publishToTelegram(e.emergency_id, ev)}
                        className={`w-8 h-8 flex items-center justify-center transition-colors ${e.telegram_published ? 'bg-[#0088cc15]' : 'hover:bg-[#0088cc15]'}`}
                        style={{ border: "2px solid #0088cc" }}
                        title={e.telegram_published ? "Ya publicado en Telegram" : "Publicar en Telegram"}
                        data-testid={`telegram-emergency-${e.emergency_id}`}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill={e.telegram_published ? "#0088cc" : "none"} stroke="#0088cc" strokeWidth="2">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={(ev) => publishToWhatsApp(e, ev)}
                        className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#25D36615]"
                        style={{ border: "2px solid #25D366" }}
                        title="Publicar en WhatsApp"
                        data-testid={`whatsapp-emergency-${e.emergency_id}`}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#25D366">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </button>
                      <ShareEmergencyButton emergency={e} brutalist className="shrink-0" />
                      <button
                        onClick={(ev) => { ev.preventDefault(); deleteEmergency(e.emergency_id); }}
                        className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-white"
                        style={{ border: "2px solid #e8637a" }}
                        data-testid={`delete-emergency-${e.emergency_id}`}
                      >
                        <Trash2 className="w-4 h-4" style={{ color: "#e8637a" }} />
                      </button>
                    </div>
                  </div>
                </Link>
              ))}
              {emergencies.length === 0 && (
                <div className="text-center py-8">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: "#ccc" }} />
                  <p className="text-sm" style={{ color: "#999" }}>No hay emergencias</p>
                </div>
              )}
            </div>
          </BCard>
        )}

        {/* Emergency Personnel Tab */}
        {activeTab === "personnel" && (
          <BCard className="p-5 md:p-6">
            <h3 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                <Siren className="w-4 h-4 text-white" />
              </div>
              PERSONAL DE EMERGENCIAS
            </h3>

            {/* Pending Verifications */}
            {emergencyPersonnel.pending?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#f64e0e" }}>
                  PENDIENTES DE VERIFICACIÓN ({emergencyPersonnel.pending.length})
                </h4>
                <div className="space-y-3">
                  {emergencyPersonnel.pending.map((p) => (
                    <div
                      key={p.user_id}
                      className="p-4"
                      style={{ background: "#f64e0e10", border: "2px solid #f64e0e" }}
                      data-testid={`pending-personnel-${p.user_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <div className="w-10 h-10 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                              <Siren className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <span className="text-base font-bold block" style={{ color: "#2D3561" }}>{p.name}</span>
                              <span className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>{p.email}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 p-3" style={{ background: "#fff", border: "2px solid #2D3561" }}>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>CUERPO</p>
                              <p className="text-sm font-bold" style={{ color: "#2D3561" }}>{CORPS_LABELS[p.emergency_corps] || p.emergency_corps}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>MODO DISPLAY</p>
                              <p className="text-sm" style={{ color: "#2D3561" }}>{p.emergency_display_mode === "anonymous" ? "Anónimo" : "Con cuerpo"}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>REGISTRO</p>
                              <p className="text-sm" style={{ color: "#2D3561" }}>{new Date(p.created_at).toLocaleDateString('es-ES')}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>UBICACIÓN</p>
                              <p className="text-sm" style={{ color: "#2D3561" }}>{p.location_name || "No especificada"}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 shrink-0 md:flex-col">
                          <BBtn
                            onClick={() => openVerificationModal(p, "verify")}
                            variant="outline"
                            testId={`verify-personnel-${p.user_id}`}
                            style={{ border: "2px solid #8B9A2D", color: "#8B9A2D" }}
                          >
                            <CheckCircle className="w-3 h-3" /> VERIFICAR
                          </BBtn>
                          <BBtn
                            onClick={() => openVerificationModal(p, "reject")}
                            variant="ghost"
                            testId={`reject-personnel-${p.user_id}`}
                          >
                            <X className="w-3 h-3" /> RECHAZAR
                          </BBtn>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Role Change Requests - Existing users wanting emergency role */}
            {emergencyPersonnel.role_requests?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#686af3" }}>
                  SOLICITUDES DE CAMBIO DE ROL ({emergencyPersonnel.role_requests.length})
                </h4>
                <p className="text-[10px] mb-3" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                  // USUARIOS EXISTENTES QUE QUIEREN SER PERSONAL DE EMERGENCIAS
                </p>
                <div className="space-y-3">
                  {emergencyPersonnel.role_requests.map((p) => (
                    <div
                      key={p.user_id}
                      className="p-4"
                      style={{ background: "#686af315", border: "2px solid #686af3" }}
                      data-testid={`role-request-${p.user_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <div className="w-10 h-10 flex items-center justify-center" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <span className="text-base font-bold block" style={{ color: "#2D3561" }}>{p.name}</span>
                              <span className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>{p.email}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 p-3" style={{ background: "#fff", border: "2px solid #2D3561" }}>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>ROL ACTUAL</p>
                              <p className="text-sm font-bold" style={{ color: "#2D3561" }}>{p.original_role === "protectora" ? "PROTECTORA" : "VOLUNTARIO"}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#f64e0e" }}>QUIERE SER</p>
                              <p className="text-sm font-bold" style={{ color: "#f64e0e" }}>{CORPS_LABELS[p.emergency_role_request_corps] || p.emergency_role_request_corps}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>MODO DISPLAY</p>
                              <p className="text-sm" style={{ color: "#2D3561" }}>{p.emergency_role_request_display_mode === "anonymous" ? "Anónimo" : "Con cuerpo"}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>SOLICITADO</p>
                              <p className="text-sm" style={{ color: "#2D3561" }}>{new Date(p.emergency_role_requested_at).toLocaleDateString('es-ES')}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 shrink-0 md:flex-col">
                          <BBtn
                            onClick={() => openVerificationModal(p, "verify", true)}
                            variant="outline"
                            testId={`approve-role-${p.user_id}`}
                            style={{ border: "2px solid #8B9A2D", color: "#8B9A2D" }}
                          >
                            <CheckCircle className="w-3 h-3" /> APROBAR
                          </BBtn>
                          <BBtn
                            onClick={() => openVerificationModal(p, "reject", true)}
                            variant="ghost"
                            testId={`reject-role-${p.user_id}`}
                          >
                            <X className="w-3 h-3" /> RECHAZAR
                          </BBtn>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verified Personnel */}
            <div>
              <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                VERIFICADOS ({emergencyPersonnel.verified?.length || 0})
              </h4>
              {emergencyPersonnel.verified?.length > 0 ? (
                <div className="space-y-3">
                  {emergencyPersonnel.verified.map((p) => (
                    <div
                      key={p.user_id}
                      className="flex items-center justify-between p-4"
                      style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                      data-testid={`verified-personnel-${p.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #2D3561" }}>
                          <Siren className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-bold" style={{ color: "#2D3561" }}>{p.name}</p>
                          <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>{p.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <BBadge bg="#8B9A2D">{CORPS_LABELS[p.emergency_corps] || p.emergency_corps}</BBadge>
                        <BBadge bg="#2D3561">VERIFICADO</BBadge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Siren className="w-10 h-10 mx-auto mb-3" style={{ color: "#ccc" }} />
                  <p className="text-sm" style={{ color: "#999" }}>No hay personal verificado</p>
                </div>
              )}
            </div>

            {emergencyPersonnel.pending?.length === 0 && emergencyPersonnel.role_requests?.length === 0 && emergencyPersonnel.verified?.length === 0 && (
              <div className="text-center py-8">
                <Siren className="w-12 h-12 mx-auto mb-3" style={{ color: "#ccc" }} />
                <p className="text-sm font-bold" style={{ color: "#2D3561" }}>NO HAY PERSONAL DE EMERGENCIAS</p>
                <p className="text-[10px] tracking-widest mt-1" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                  // CUANDO SE REGISTREN BOMBEROS, POLICÍAS, ETC. APARECERÁN AQUÍ
                </p>
              </div>
            )}
          </BCard>
        )}

        {/* Activity Tab */}
        {activeTab === "activity" && (
          <BCard className="p-5 md:p-6">
            <h3 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#a88efb", border: "2px solid #2D3561" }}>
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              ÚLTIMOS 7 DÍAS
            </h3>
            <div className="space-y-3">
              {activity.map((day) => (
                <div
                  key={day.date}
                  className="flex items-center justify-between p-4"
                  style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                >
                  <div className="text-sm font-bold" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                    {new Date(day.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#686af3" }}>{day.new_users}</p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#999" }}>USUARIOS</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#f64e0e" }}>{day.new_emergencies}</p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#999" }}>EMERGENCIAS</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#e8637a" }}>{day.new_offers}</p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#999" }}>AYUDAS</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#a88efb" }}>{day.messages}</p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#999" }}>MENSAJES</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </BCard>
        )}

        {/* Moderation Tab */}
        {activeTab === "moderation" && (
          <BCard className="p-5 md:p-6">
            <h3 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2" style={{ color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#e8637a", border: "2px solid #2D3561" }}>
                <Shield className="w-4 h-4 text-white" />
              </div>
              AVISTAMIENTOS REPORTADOS
            </h3>
            {reportedSightings.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: "#8B9A2D" }} />
                <p className="text-sm font-bold" style={{ color: "#2D3561" }}>NO HAY PENDIENTES</p>
                <p className="text-[10px] tracking-widest mt-1" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                  // LA COMUNIDAD SE PORTA BIEN
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {reportedSightings.map((s) => (
                  <div
                    key={s.sighting_id}
                    className="p-4"
                    style={{ background: "#f8f7f4", border: "2px solid #2D3561", borderLeft: "6px solid #f64e0e" }}
                    data-testid={`moderation-sighting-${s.sighting_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Eye className="w-4 h-4" style={{ color: "#686af3" }} />
                          <span className="text-sm font-bold" style={{ color: "#2D3561" }}>{s.user_name}</span>
                          <BBadge bg="#f64e0e">
                            <Flag className="w-2.5 h-2.5 mr-0.5 inline" />
                            {s.report_count} REPORTE{s.report_count !== 1 ? "S" : ""}
                          </BBadge>
                          {s.status === "rechazado" && <BBadge bg="#999">RECHAZADO</BBadge>}
                        </div>
                        <p className="text-sm" style={{ color: "#666" }}>{s.description}</p>
                        <p className="text-[10px] tracking-wider mt-2" style={{ fontFamily: B.mono, color: "#999" }}>
                          {s.location_name} · {new Date(s.created_at).toLocaleString('es-ES')}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {s.status !== "verificado" && (
                          <BBtn
                            onClick={() => moderateSighting(s.sighting_id, "verificado")}
                            variant="outline"
                            testId={`verify-sighting-${s.sighting_id}`}
                            style={{ border: "2px solid #8B9A2D", color: "#8B9A2D" }}
                          >
                            <CheckCircle className="w-3 h-3" /> VERIFICAR
                          </BBtn>
                        )}
                        {s.status !== "rechazado" && (
                          <BBtn
                            onClick={() => moderateSighting(s.sighting_id, "rechazado")}
                            variant="ghost"
                            testId={`reject-sighting-${s.sighting_id}`}
                          >
                            <X className="w-3 h-3" /> RECHAZAR
                          </BBtn>
                        )}
                        <BBtn
                          onClick={() => banUser(s.user_id, false)}
                          variant="danger"
                          testId={`ban-user-${s.user_id}`}
                        >
                          <Ban className="w-3 h-3" /> BANEAR
                        </BBtn>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </BCard>
        )}

        {/* Shares Tab */}
        {activeTab === "shares" && (
          <BCard className="p-5 md:p-6">
            <h3 className="text-lg font-bold tracking-tight mb-6 flex items-center gap-2" style={{ color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #2D3561" }}>
                <Share2 className="w-4 h-4 text-white" />
              </div>
              ESTADÍSTICAS DE DIFUSIÓN
            </h3>

            {shareStats ? (
              <div className="space-y-6">
                {/* Network breakdown */}
                <div>
                  <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#a88efb" }}>POR RED SOCIAL</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                      { id: "whatsapp", name: "WhatsApp", color: "#25D366" },
                      { id: "facebook", name: "Facebook", color: "#1877F2" },
                      { id: "twitter", name: "X/Twitter", color: "#000" },
                      { id: "telegram", name: "Telegram", color: "#0088CC" },
                      { id: "copy", name: "Copiado", color: "#6b7280" },
                    ].map((net) => (
                      <div
                        key={net.id}
                        className="p-4 text-center"
                        style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                      >
                        <p className="text-2xl font-bold" style={{ fontFamily: B.mono, color: net.color }}>
                          {shareStats.by_network?.[net.id] || 0}
                        </p>
                        <p className="text-[9px] tracking-widest mt-1" style={{ fontFamily: B.mono, color: "#999" }}>{net.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Type breakdown */}
                <div>
                  <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#a88efb" }}>POR TIPO</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-4" style={{ background: "#e8637a15", border: "2px solid #e8637a" }}>
                      <p className="text-2xl font-bold" style={{ fontFamily: B.mono, color: "#e8637a" }}>
                        {shareStats.by_type?.lost_pet || 0}
                      </p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#666" }}>PERDIDOS</p>
                    </div>
                    <div className="p-4" style={{ background: "#686af315", border: "2px solid #686af3" }}>
                      <p className="text-2xl font-bold" style={{ fontFamily: B.mono, color: "#686af3" }}>
                        {shareStats.by_type?.need || 0}
                      </p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#666" }}>NECESIDADES</p>
                    </div>
                    <div className="p-4" style={{ background: "#f64e0e15", border: "2px solid #f64e0e" }}>
                      <p className="text-2xl font-bold" style={{ fontFamily: B.mono, color: "#f64e0e" }}>
                        {shareStats.by_type?.emergency || 0}
                      </p>
                      <p className="text-[9px] tracking-widest" style={{ fontFamily: B.mono, color: "#666" }}>EMERGENCIAS</p>
                    </div>
                  </div>
                </div>

                {/* Top shared pets */}
                {shareStats.top_shared_pets?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                      ANIMALES MÁS DIFUNDIDOS
                    </h4>
                    <div className="space-y-2">
                      {shareStats.top_shared_pets.map((pet, idx) => (
                        <div
                          key={pet.pet_id}
                          className="flex items-center gap-3 p-3"
                          style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                        >
                          <span className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#ccc" }}>#{idx + 1}</span>
                          <img
                            src={pet.photo_url}
                            alt={pet.name}
                            className="w-10 h-10 object-cover"
                            style={{ border: "2px solid #2D3561" }}
                            onError={(e) => { e.target.src = 'https://placehold.co/40/f3f4f6/9ca3af?text=?'; }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold truncate" style={{ color: "#2D3561" }}>{pet.name}</p>
                          </div>
                          <BBadge bg="#8B9A2D">
                            <Share2 className="w-3 h-3 mr-1 inline" />
                            {pet.share_count}
                          </BBadge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top shared needs */}
                {shareStats.top_shared_needs?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold tracking-widest mb-3" style={{ fontFamily: B.mono, color: "#a88efb" }}>
                      NECESIDADES MÁS DIFUNDIDAS
                    </h4>
                    <div className="space-y-2">
                      {shareStats.top_shared_needs.map((need, idx) => (
                        <div
                          key={need.need_id}
                          className="flex items-center gap-3 p-3"
                          style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}
                        >
                          <span className="text-lg font-bold" style={{ fontFamily: B.mono, color: "#ccc" }}>#{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold truncate" style={{ color: "#2D3561" }}>{need.title}</p>
                          </div>
                          <BBadge bg="#686af3">
                            <Share2 className="w-3 h-3 mr-1 inline" />
                            {need.share_count}
                          </BBadge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Share2 className="w-12 h-12 mx-auto mb-3" style={{ color: "#ccc" }} />
                <p className="text-sm" style={{ color: "#999" }}>No hay datos de compartido todavía</p>
              </div>
            )}
          </BCard>
        )}
      </div>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent className="sm:max-w-md" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
                <Bell className="w-4 h-4 text-white" />
              </div>
              NOTIFICACIÓN MASIVA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESTINATARIOS</Label>
              <Select
                value={broadcastForm.target}
                onValueChange={(v) => setBroadcastForm({ ...broadcastForm, target: v })}
              >
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="broadcast-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 0, border: B.border }}>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  <SelectItem value="volunteers">Solo voluntarios</SelectItem>
                  <SelectItem value="shelters">Solo protectoras</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={broadcastForm.title}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                placeholder="Aviso importante"
                className="mt-1"
                style={inputStyle}
                data-testid="broadcast-title"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>MENSAJE</Label>
              <Textarea
                value={broadcastForm.message}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                placeholder="Escribe tu mensaje aquí..."
                className="mt-1 min-h-[100px]"
                style={inputStyle}
                data-testid="broadcast-message"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <BBtn
              variant="outline"
              onClick={() => setShowBroadcast(false)}
            >
              CANCELAR
            </BBtn>
            <BBtn
              onClick={sendBroadcast}
              variant="primary"
              testId="send-broadcast-btn"
            >
              <Send className="w-4 h-4" />
              ENVIAR
            </BBtn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Official Emergency Dialog */}
      <Dialog open={showCreateEmg} onOpenChange={setShowCreateEmg}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              {editingEmgId ? "EDITAR EMERGENCIA" : "CREAR EMERGENCIA OFICIAL"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingEmgId && (
              <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>
                Se crea como BORRADOR. Después podrás distribuirla internamente o publicarla en la web.
              </p>
            )}
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO *</Label>
              <Input
                value={emgForm.title}
                onChange={(e) => setEmgForm({ ...emgForm, title: e.target.value })}
                placeholder="Incendio forestal en la Sierra de..."
                className="mt-1"
                style={inputStyle}
                data-testid="emg-form-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TIPO</Label>
                <Select value={emgForm.type} onValueChange={(v) => setEmgForm({ ...emgForm, type: v })}>
                  <SelectTrigger className="mt-1" style={selectStyle} data-testid="emg-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ borderRadius: 0, border: B.border }}>
                    {EMG_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>PAÍS</Label>
                <Select value={emgForm.country} onValueChange={(v) => setEmgForm({ ...emgForm, country: v })}>
                  <SelectTrigger className="mt-1" style={selectStyle} data-testid="emg-form-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ borderRadius: 0, border: B.border }}>
                    {EMG_COUNTRIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>ZONA / UBICACIÓN</Label>
              <Input
                value={emgForm.location_name}
                onChange={(e) => setEmgForm({ ...emgForm, location_name: e.target.value })}
                placeholder="Valencia, Comunidad Valenciana"
                className="mt-1"
                style={inputStyle}
                data-testid="emg-form-location"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={emgForm.description}
                onChange={(e) => setEmgForm({ ...emgForm, description: e.target.value })}
                placeholder="Descripción de la emergencia, zonas afectadas, cómo ayudar..."
                className="mt-1 min-h-[100px]"
                style={inputStyle}
                data-testid="emg-form-description"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>IMÁGENES (INFOGRAFÍAS, FOTOS)</Label>
              <label
                className="mt-1 flex items-center justify-center gap-2 py-3 cursor-pointer text-xs font-bold uppercase tracking-wider"
                style={{ fontFamily: B.mono, border: "2px dashed #2D3561", color: "#2D3561", opacity: uploadingEmgImage ? 0.5 : 1 }}
                data-testid="emg-form-image-upload"
              >
                <ImagePlus className="w-4 h-4" />
                {uploadingEmgImage ? "SUBIENDO..." : "AÑADIR IMÁGENES"}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={uploadingEmgImage}
                  onChange={(e) => { uploadEmgImages(Array.from(e.target.files)); e.target.value = ""; }}
                />
              </label>
              {emgForm.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {emgForm.images.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={fileUrl(img)} alt={`Imagen ${i + 1}`} className="w-16 h-16 object-cover" style={{ border: "2px solid #2D3561" }} />
                      <button
                        onClick={() => setEmgForm({ ...emgForm, images: emgForm.images.filter((_, j) => j !== i) })}
                        className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center"
                        style={{ background: "#e8637a", border: "2px solid #2D3561" }}
                        data-testid={`emg-form-remove-image-${i}`}
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 mt-4">
            <BBtn variant="outline" onClick={() => setShowCreateEmg(false)}>CANCELAR</BBtn>
            <BBtn onClick={createOfficialEmergency} disabled={creatingEmg || uploadingEmgImage} variant="secondary" testId="emg-form-submit">
              <Plus className="w-4 h-4" />
              {creatingEmg ? "GUARDANDO..." : editingEmgId ? "GUARDAR CAMBIOS" : "CREAR BORRADOR"}
            </BBtn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup Test Data Confirm Dialog */}
      <Dialog open={!!cleanupPreview} onOpenChange={(open) => !open && setCleanupPreview(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#e8637a", border: "2px solid #2D3561" }}>
                <Trash2 className="w-4 h-4 text-white" />
              </div>
              LIMPIAR DATOS DE PRUEBA
            </DialogTitle>
          </DialogHeader>
          {cleanupPreview && (
            <div className="space-y-4">
              <p className="text-xs" style={{ color: "#2D3561" }}>
                Se eliminarán <strong>{cleanupPreview.users.length} usuarios de prueba</strong>, <strong>{cleanupPreview.emergencies.length} emergencias de prueba</strong> y <strong>{cleanupPreview.needs_count} necesidades</strong> con todos sus datos asociados. Esta acción no se puede deshacer.
              </p>
              {cleanupPreview.users.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold tracking-widest mb-1" style={{ fontFamily: B.mono, color: "#e8637a" }}>USUARIOS ({cleanupPreview.users.length})</p>
                  <div className="max-h-40 overflow-y-auto p-2" style={{ border: "2px solid #2D3561", background: "#f8f7f4" }}>
                    {cleanupPreview.users.map((u) => (
                      <p key={u.user_id} className="text-[10px]" style={{ fontFamily: B.mono, color: "#555" }}>{u.name} — {u.email}</p>
                    ))}
                  </div>
                </div>
              )}
              {cleanupPreview.emergencies.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold tracking-widest mb-1" style={{ fontFamily: B.mono, color: "#e8637a" }}>EMERGENCIAS ({cleanupPreview.emergencies.length})</p>
                  <div className="max-h-32 overflow-y-auto p-2" style={{ border: "2px solid #2D3561", background: "#f8f7f4" }}>
                    {cleanupPreview.emergencies.map((e) => (
                      <p key={e.emergency_id} className="text-[10px]" style={{ fontFamily: B.mono, color: "#555" }}>{e.title}</p>
                    ))}
                  </div>
                </div>
              )}
              {cleanupPreview.users.length === 0 && cleanupPreview.emergencies.length === 0 && (
                <p className="text-xs" style={{ color: "#8B9A2D" }}>No se han encontrado datos de prueba. Todo limpio.</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 mt-4">
            <BBtn variant="outline" onClick={() => setCleanupPreview(null)}>CANCELAR</BBtn>
            {cleanupPreview && (cleanupPreview.users.length > 0 || cleanupPreview.emergencies.length > 0) && (
              <BBtn onClick={confirmCleanup} disabled={cleaning} variant="danger" testId="confirm-cleanup-btn">
                <Trash2 className="w-4 h-4" />
                {cleaning ? "ELIMINANDO..." : "ELIMINAR TODO"}
              </BBtn>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verification Modal for Emergency Personnel */}
      <Dialog open={verificationModal.open} onOpenChange={(open) => !open && closeVerificationModal()}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: verificationModal.action === "verify" ? "#8B9A2D" : "#e8637a", border: "2px solid #2D3561" }}>
                {verificationModal.action === "verify" ? <CheckCircle className="w-4 h-4 text-white" /> : <X className="w-4 h-4 text-white" />}
              </div>
              {verificationModal.action === "verify" ? "VERIFICAR PERSONAL" : "RECHAZAR SOLICITUD"}
            </DialogTitle>
          </DialogHeader>
          
          {verificationModal.user && (
            <div className="space-y-4">
              {/* User Info Card */}
              <div className="p-4" style={{ background: "#f8f7f4", border: "2px solid #2D3561" }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 flex items-center justify-center" style={{ background: "#f64e0e", border: "2px solid #2D3561" }}>
                    <Siren className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold" style={{ color: "#2D3561" }}>{verificationModal.user.name}</p>
                    <BBadge bg="#f64e0e">{CORPS_LABELS[verificationModal.user.emergency_corps] || verificationModal.user.emergency_corps}</BBadge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" style={{ color: "#a88efb" }} />
                    <span className="text-sm" style={{ color: "#2D3561" }}>{verificationModal.user.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: "#a88efb" }} />
                    <span className="text-sm" style={{ color: "#2D3561" }}>
                      {new Date(verificationModal.user.created_at).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" style={{ color: "#a88efb" }} />
                    <span className="text-sm" style={{ color: "#2D3561" }}>
                      {verificationModal.user.location_name || "Sin ubicación"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" style={{ color: "#a88efb" }} />
                    <span className="text-sm" style={{ color: "#2D3561" }}>
                      {verificationModal.user.emergency_display_mode === "anonymous" ? "Anónimo" : "Con cuerpo visible"}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Info message */}
              <div className="p-3" style={{ background: verificationModal.action === "verify" ? "#8B9A2D15" : "#e8637a15", border: `2px solid ${verificationModal.action === "verify" ? "#8B9A2D" : "#e8637a"}` }}>
                <p className="text-xs" style={{ fontFamily: B.mono, color: verificationModal.action === "verify" ? "#8B9A2D" : "#e8637a" }}>
                  {verificationModal.action === "verify" 
                    ? "Al verificar, esta persona podrá crear alertas y registrar animales rescatados. Sus publicaciones aparecerán como \"Personal de Emergencias\"."
                    : "Al rechazar, esta persona recibirá una notificación con el motivo. Podrá volver a solicitarlo en el futuro."
                  }
                </p>
              </div>
              
              {/* Notes/Reason field */}
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                  {verificationModal.action === "verify" ? "NOTAS (OPCIONAL)" : "MOTIVO DEL RECHAZO"}
                </Label>
                <Textarea
                  value={verificationNote}
                  onChange={(e) => setVerificationNote(e.target.value)}
                  placeholder={verificationModal.action === "verify" 
                    ? "Añade notas internas si lo deseas..." 
                    : "Ej: No se pudo verificar la identidad, documentación insuficiente..."
                  }
                  className="mt-1 min-h-[80px]"
                  style={inputStyle}
                  data-testid="verification-note"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <BBtn
              variant="outline"
              onClick={closeVerificationModal}
              disabled={processingVerification}
            >
              CANCELAR
            </BBtn>
            <BBtn
              onClick={confirmVerification}
              disabled={processingVerification}
              variant={verificationModal.action === "verify" ? "primary" : "secondary"}
              testId="confirm-verification-btn"
              style={verificationModal.action === "verify" 
                ? { background: "#8B9A2D", border: B.border, boxShadow: B.shadowSm }
                : { background: "#e8637a", border: B.border, boxShadow: B.shadowSm }
              }
            >
              {processingVerification 
                ? "PROCESANDO..." 
                : verificationModal.action === "verify" 
                  ? <><CheckCircle className="w-4 h-4" /> CONFIRMAR VERIFICACIÓN</>
                  : <><X className="w-4 h-4" /> CONFIRMAR RECHAZO</>
              }
            </BBtn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatsCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <div className="bg-white p-4" style={{ border: B.border, boxShadow: B.shadowSm }} data-testid={`stats-${label.toLowerCase().replace(' ', '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[9px] font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#a88efb" }}>{label}</p>
          <p className="text-2xl font-bold mt-1" style={{ fontFamily: B.font, color: "#2D3561" }}>{value}</p>
          <p className="text-[9px] tracking-wider mt-1" style={{ fontFamily: B.mono, color: "#999" }}>{subtext}</p>
        </div>
        <div className="w-9 h-9 flex items-center justify-center" style={{ background: color, border: "2px solid #2D3561" }}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
}
